import { Controller, useFormContext } from 'react-hook-form';

import { CollapsiblePanelProvider } from '../../../../../../components/CarFinancePage/components/Panels/shared/CollapsiblePanel/CollapsiblePanel.context';

import { CollapsiblePanel } from '../../../../../../components/CarFinancePage/components/Panels/shared/CollapsiblePanel/CollapsiblePanel';
import { FinanceCheckbox } from '../../../../../../components/CarFinancePage/financeProviders/shared/FinanceCheckbox';
import { Text } from '../../../../../../components/ToolkitV2/Text/Text';
import { Link } from '../../../../../../components/Toolkit/Button/Link';
import { Notification } from '../../../../../../components/Toolkit/Notification/Notification';
import { ListNative } from '../../../../../../components/ToolkitV2/ListNative/ListNative';

import { Spacer } from '../../../../../../helpers/genericStyles';

import { type Form } from '../../../../../../components/CarFinancePage/queries/schema/carFinance.schema';

const DataProtectionAndConsents = () => {
  const { control } = useFormContext<Form>();

  return (
    <CollapsiblePanelProvider panelName="consent">
      <CollapsiblePanel
        headerText="Data Protection and Consents"
        accordionName="Data Protection and Consents"
        mb={['S8', 'M16']}
        panelName="consent"
      >
        <Spacer marginBottom="M24" />
        {/* TODO: add link hrefs once they are provided by BOI */}
        <Text color="GREY_DARKER">
          Please indicate that you agree with the following:
          <Link href="" target="_blank" rel="nofollow">
            Bank of Ireland Data Privacy Notice
          </Link>
          <Link href="" target="_blank" rel="nofollow">
            Bank of Ireland Terms of Business
          </Link>
        </Text>
        <Spacer marginBottom="S12" />
        <Controller
          name="consent.acceptDocuments"
          control={control}
          render={({
            field: { onChange, name, ref, value, onBlur },
            fieldState,
          }) => (
            <FinanceCheckbox
              name={name}
              ref={ref}
              onChange={onChange}
              onBlur={onBlur}
              checked={value}
              {...fieldState}
              label={
                <Text weight="bold" color="GREY_DARKER">
                  I confirm that I have read and accept the above documents
                </Text>
              }
            />
          )}
        />
        <Spacer marginBottom="S4" />
        <Text forwardedAs="h3" weight="bold" size="M16" color="GREY_DARKER">
          Acknowledgement
        </Text>
        <Spacer marginBottom="M16" />
        <Text color="GREY_DARKER">
          In order to provide you with a timely response and to allow us assess
          risk fairly and consistently, we may use automated decision making. If
          you do not agree with the result, you have the right to provide us
          with your point of view and have those decisions reviewed by a member
          of our team. Where you select a Motor Dealer above and complete an
          application with Bank of Ireland Finance, you are confirming that the
          Motor Dealer nominated by you will act as your credit intermediary for
          the purposes of any credit application or credit agreement with Bank
          of Ireland Finance. As part of any Bank of Ireland Finance credit
          application or finance contract, it is necessary for Bank of Ireland
          Finance to share the details of that credit application (including
          your personal data, where relevant) together with the bank’s decision
          in relation to that application with the Motor Dealer in their
          capacity as a credit intermediary. More detailed information regarding
          how the Bank of Ireland Group processes personal data is available in
          the Bank of Ireland Data Privacy Notice which is available on request
          or{' '}
          <a
            href="https://www.bankofireland.com/privacy/data-protection-notice/"
            target="_blank"
            rel="nofollow"
          >
            https://www.bankofireland.com/privacy
          </a>
          {/* */}.
        </Text>
        <Spacer marginBottom="M24" />
        <Text forwardedAs="h3" weight="bold" size="M16" color="GREY_DARKER">
          Credit Reference Agencies
        </Text>
        <Spacer marginBottom="M16" />
        <Text color="GREY_DARKER">
          As part of the application process and ongoing finance agreement
          management we will carry out credit checks and credit scoring and
          share information with the Central Credit Register (CCR), Irish Credit
          Bureau (ICB) and/or other credit reference agencies. Those agencies
          may keep a record of this information and may give it to other
          financial institutions that you apply to for credit facilities. The
          ICB uses Legitimate Interests (GDPR Article 6 (f)) as the legal basis
          for processing of your personal and credit information. These
          Legitimate Interests are promoting greater financial stability by
          supporting a full and accurate assessment of loan applications, aiding
          in the avoidance of over-indebtedness, assisting in lowering the cost
          of credit, complying with and supporting compliance with legal and
          regulatory requirements, enabling more consistent, faster
          decision-making in the provision of credit and assisting in fraud
          prevention. Please review ICB’s Fair Processing Notice which is
          available at{' '}
          <a
            href="" // TODO: update this once a new link is provided
            target="_blank"
            rel="nofollow"
          >
            http://www.icb.ie/pdf/Fair Processing Notice.pdf
          </a>
          {/* */}.
        </Text>
        <Spacer marginBottom="M16" />
        <Notification intent="INFO" alignItems="flex-start">
          NOTICE: Under the Credit Reporting Act 2013 lenders are required to
          provide personal and credit information for credit applications and
          credit agreements of €500 and above to the Central Credit Register.
          This information will be held on the Central Credit Register and may
          be used by other lenders when making decisions on your credit
          applications and credit agreements.
        </Notification>
        <Spacer marginBottom="M24" />
        <Text color="GREY_DARKER">
          The Central Credit Register is owned and operated by the Central Bank
          of Ireland. For more information, including on how your data is
          processed, see{' '}
          <a
            href="https://www.centralcreditregister.ie"
            target="_blank"
            rel="nofollow"
          >
            www.centralcreditregister.ie
          </a>
          {/* */}.
        </Text>
        <Spacer marginBottom="M16" />
        <Text>Under the Credit Reporting Act you are entitled to:</Text>
        <ListNative
          name="credit-reporting-act-entitlements"
          markerColour="GREY_DARKER"
        >
          <Text color="GREY_DARKER">
            get a copy of your credit record from the Central Bank (for sole
            traders and individuals this will be free and for companies the
            first credit report each calendar year is free);
          </Text>
          <Text color="GREY_DARKER">
            correct any errors on your credit record;
          </Text>
          <Text color="GREY_DARKER">
            tell the Central Bank if you suspect you may have been impersonated
            and
          </Text>
          <Text color="GREY_DARKER">
            ask the Central Bank to add a short explanation written by you to
            your credit record.
          </Text>
        </ListNative>
        <Spacer marginBottom="M16" />
        <Text color="GREY_DARKER">
          To learn more about the register, and your rights and duties under the
          Credit Reporting Act 2013, please see{' '}
          <a
            href="https://www.centralcreditregister.ie"
            target="_blank"
            rel="nofollow"
          >
            www.centralcreditregister.ie
          </a>
          {/* */}.
        </Text>
        <Spacer marginBottom="M16" />
        <Text color="GREY_DARKER">
          As per the DoneDeal{' '}
          <a
            href="https://hello.donedeal.ie/hc/en-us/articles/201251721-Privacy-Cookie-Policies"
            target="_blank"
          >
            privacy policy
          </a>
          {/* */}, DoneDeal will retain your data for the purposes of assisting
          with your finance application and will act, in addition to Bank of
          Ireland, as a Data Controller of your data.
        </Text>
        <Spacer marginBottom="M16" />
        <Controller
          name="consent.dataConsent"
          control={control}
          render={({
            field: { onChange, name, ref, value, onBlur },
            fieldState,
          }) => (
            <FinanceCheckbox
              name={name}
              ref={ref}
              onChange={onChange}
              onBlur={onBlur}
              checked={value}
              {...fieldState}
              label={
                <Text weight="bold" color="GREY_DARKER">
                  I agree with the above terms and conditions and consent to the
                  details of my finance application being submitted to DoneDeal
                  and Bank of Ireland.
                </Text>
              }
            />
          )}
        />
      </CollapsiblePanel>
    </CollapsiblePanelProvider>
  );
};

export { DataProtectionAndConsents };
